import React, {useState} from 'react';
import Select from '@components/base/Select/Select';
import TextBox from "@components/base/TextBox/TextBox";
import Button from "@components/base/Button/Button";
import Loading from "@components/widgets/Loading/Loading";
import styles from "./MigrationFormModal.scss";
import {_validateEmail, _validateIsEmpty, _validatePhoneNumber, _sanitizePhoneNumber} from "@helpers/HelperValidator";
import {ContextOperatorTaskProvider, useOperatorTaskContext} from '@contexts/ContextOperatorTaskProvider';
import Modal from "@components/base/Modal/Modal";
import MigrationFormModalData from "./data/MigrationFormModalData";
import {notify} from "@contexts/ContextNotificationProvider";

// Modal with content
const MigrationFormModal = ({color = ''}) => {

	const [isModalOpen, setIsModalOpen] = useState(true);

	if (!isModalOpen) {
		return null;
	}

	return (
	  <Modal backgroundColor={'white'} onClose={() => setIsModalOpen(false)}>
		  <ContextOperatorTaskProvider>
			  <Content data={MigrationFormModalData} color={color} closeModal={() => setIsModalOpen(false)}/>
		  </ContextOperatorTaskProvider>
	  </Modal>
	)
}

// Content of the modal
const Content = ({
					 data,
					 color = '',
					 closeModal = () => {
					 }
				 }) => {
	const {
		formTitle,
		helpText,
		buttonText,
		successText,
		errorMsg,
		formLabels
	} = data;

	const {contextData, CreateMigrationTask} = useOperatorTaskContext();

	const defaultFormState = {
		name: '',
		email: '',
		phoneCode: '',
		phone: '',
		domain: ''
	};
	const phoneCodes = contextData.phoneCodes ?? [];

	const [formState, setFormState] = useState({...defaultFormState, phoneCode: 'BG'});
	const [errorFormState, setErrorFormState] = useState({...defaultFormState});
	const [showSuccessMessage, setShowSuccessMessage] = useState(false);
	const [loading, setLoading] = useState(false);

	const onChange = (key, value) => {
		// Set form state.
		formState[key] = key === 'phone' ? _sanitizePhoneNumber(value) : value;
		setFormState({...formState});
		// Clear error state.
		errorFormState[key] = '';
		setErrorFormState({...errorFormState});
	}

	const handleSuccess = () => {
		setLoading(false);
		setShowSuccessMessage(true);
	}

	const handleError = (message) => {
		setLoading(false);
		closeModal();
		// Let the modal finish unmounting before the centered alert appears,
		// so the two transitions are staggered instead of colliding.
		setTimeout(() => notify.alert(message), 250);
	}

	const submit = () => {
		setErrorFormState({...defaultFormState});

		let current = {};
		if (!_validateIsEmpty(formState.name)) {
			current.name = errorMsg.emptyName || '';
		}
		if (!_validateIsEmpty(formState.email)) {
			current.email = errorMsg.emptyEmail || '';
		}
		if (!_validateEmail(formState.email)) {
			current.email = errorMsg.invalidEmail || '';
		}
		if (!_validateIsEmpty(formState.phoneCode)) {
			current.phoneCode = errorMsg.emptyPhoneCode || '';
		}
		if (!_validateIsEmpty(formState.phone)) {
			current.phone = errorMsg.emptyPhone || '';
		} else if (!_validatePhoneNumber(formState.phone)) {
			current.phone = errorMsg.badPhoneNumber || '';
		}
		if (!_validateIsEmpty(formState.domain)) {
			current.domain = errorMsg.emptyDomain || '';
		}

		if (Object.keys(current).length !== 0) {
			setErrorFormState({...errorFormState, ...current});
			return;
		}

		setLoading(true);
		CreateMigrationTask(formState, handleSuccess, handleError);
	}

	// Show loading animation
	if (loading) {
		return (<div className={styles.container}><Loading/></div>);
	}

	// Show success message
	if (showSuccessMessage && successText) {
		return (
		  <div className={styles.container}>
			  {successText.map((text, key) => (
				<p key={key} className={styles.successMessage}>{text}</p>
			  ))}
		  </div>
		);
	}

	return (
	  <div className={styles.container} data-modal-container="true">
		  <h3>{formTitle}</h3>

		  {/*Additional info*/}
		  <div className={`${styles.row}`}>
			  <div/>
			  <div>
				  <span className={styles.required}>
					  {helpText}
				  </span>
			  </div>
		  </div>

		  {/*Name*/}
		  <div className={styles.row}>
			  <div>
				  <label>
					  <span className={styles.required}>*</span>
					  <span>{formLabels.name.label}</span>
				  </label>
			  </div>
			  <div>
				  <TextBox
					name={"name"}
					placeholder={formLabels.name.placeholder}
					value={formState.name}
					error={errorFormState.name}
					color={color}
					onChange={({target}) => onChange('name', target.value)}/>
			  </div>
		  </div>

		  {/*Email*/}
		  <div className={styles.row}>
			  <div>
				  <label>
					  <span className={styles.required}>*</span>
					  <span>{formLabels.email.label}</span>
				  </label>
			  </div>
			  <div>
				  <TextBox
					type={'email'}
					name={"email"}
					placeholder={'ivanivanov@mysite.bg'}
					value={formState.email}
					error={errorFormState.email}
					color={color}
					onChange={({target}) => onChange('email', target.value)}/>
			  </div>
		  </div>

		  {/*Phone*/}
		  <div className={styles.row} data-row-container="true">
			  <div>
				  <label>
					  <span className={styles.required}>*</span>
					  <span>{formLabels.phone.label}</span>
				  </label>
			  </div>
			  <div className={styles.phoneContainer}>
				  <Select
					selected={formState.phoneCode}
					name={'phone_code'}
					options={phoneCodes}
					placeholder={'999'}
					required={true}
					isGrouped={true}
					color={color}
					error={errorFormState.phoneCode}
					onChange={(selectedOption) => onChange('phoneCode', selectedOption.value)}
				  />
				  <TextBox
					type={'tel'}
					name={"phone"}
					placeholder="99 999 99 99"
					value={formState.phone}
					error={errorFormState.phone}
					color={color}
					onChange={({target}) => onChange('phone', target.value)}
				  />
			  </div>
		  </div>

		  {/*Domain*/}
		  <div className={styles.row}>
			  <div>
				  <label>
					  <span className={styles.required}>*</span>
					  <span>{formLabels.domain.label}</span>
				  </label>
			  </div>
			  <div>
				  <TextBox
					name={"domain"}
					placeholder={formLabels.domain.placeholder}
					value={formState.domain}
					error={errorFormState.domain}
					color={color}
					onChange={({target}) => onChange('domain', target.value)}/>
			  </div>
		  </div>

		  {/*Submit button*/}
		  <div className={styles.row}>
			  <div/>
			  <div>
				  <Button value={buttonText} onClick={submit}/>
			  </div>
		  </div>
	  </div>
	)
}

export default MigrationFormModal;
